import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Tag, Space, message, Empty, Button, Modal, Form, Select, InputNumber, Typography, Image, Table } from 'antd';
import { DeleteOutlined, UserAddOutlined } from '@ant-design/icons';
import {
  getUsers,
  deleteUser,
  createVirtualCollectorUser,
  // adminTestVoiceCall, // [已弃用] 占位测试外呼
  adminResendUserVoiceLowBalance,
  adminResetUserVoiceLowBalanceFlag,
  adminQueryUserVoiceCallsToday,
  adminResendUserSmsLowBalance,
  adminQueryUserSmsDetails,
} from '../api';
import { useNavigate } from 'react-router-dom';
import ListToolbar from '../components/ListToolbar';
import { exportToCSV } from '../utils/exportUtils';
import PageTitle from '../components/PageTitle';
import ResizableTable from '../components/ResizableTable';
import { formatAdminUserSelectLabel } from '../utils/userDisplay';

// 角色配置（user_type：账号基础身份）
const ROLE_CONFIG = {
  1: { label: '房东', color: 'blue' },
  2: { label: '租客', color: 'green' },
  3: { label: '管理员', color: 'red' },
  0: { label: '未知角色', color: 'gray' },
};

/** 房东在房源里指定的子管理员（展示为「管理员」+ 紫色系），与 user_type=3 的系统管理员（红）区分 */
const HOUSE_ADMIN_TAG_STYLE = {
  margin: 0,
  color: '#531dab',
  background: '#f9f0ff',
  borderColor: '#d3adf7',
};

const formatVoiceNotifyYm = (ym) => {
  const n = Number(ym);
  if (!Number.isFinite(n) || n <= 0) return '';
  const s = String(n);
  if (s.length < 6) return s;
  return `${s.slice(0, 4)}-${s.slice(4, 6)}`;
};

const truthy = (v) =>
  v === true || v === 1 || v === '1' || v === 'true' || v === 'TRUE';

const renderBackupText = (value) => (value && String(value).trim() ? value : '—');

const BACKUP_CHECKIN_STATUS_COLORS = {
  待确认入住: 'processing',
  已拒绝入住: 'error',
  已退租: 'default',
  已入住: 'success',
  未确认入住: 'warning',
};

const BACKUP_IDENTITY_STATUS_COLORS = {
  已认证: 'success',
  待房东审核: 'processing',
  未认证: 'default',
  '未认证（资料已齐）': 'gold',
};

const renderBackupStatusTag = (text, colorMap) => {
  const t = text && String(text).trim();
  if (!t || t === '—') return '—';
  return <Tag color={colorMap[t] || 'default'}>{t}</Tag>;
};

const renderBackupIdCardImage = (url, label) => {
  if (!url || !String(url).trim()) {
    return '—';
  }
  return (
    <Image
      src={url}
      alt={label}
      width={48}
      height={32}
      style={{ objectFit: 'cover', borderRadius: 4 }}
      preview={{ mask: '预览' }}
    />
  );
};

const isLandlordDesignatedHouseAdmin = (record) => {
  if (!record || typeof record !== 'object') return false;
  const r = record;
  if (truthy(r.is_house_admin)) return true;
  if (r.__isHouseAdminByHouseList === true) return true;
  if (truthy(r.isHouseAdmin) || truthy(r.IsHouseAdmin)) return true;
  if (truthy(r.is_designated_house_admin) || truthy(r.isDesignatedHouseAdmin)) return true;
  if (truthy(r.house_admin_flag) || truthy(r.house_admin)) return true;
  if (Array.isArray(r.admin_house_ids) && r.admin_house_ids.length > 0) return true;
  if (Array.isArray(r.managed_house_ids) && r.managed_house_ids.length > 0) return true;
  if (Number(r.house_admin_count) > 0 || Number(r.managed_house_count) > 0) return true;
  return false;
};

// 状态配置
const STATUS_CONFIG = {
  1: { label: '正常', color: 'success' },
  0: { label: '禁用', color: 'warning' },
};

// 筛选用的角色选项（包含“全部”）
const ROLE_FILTER_OPTIONS = [
  { value: '', label: '全部角色' },
  { value: '1', label: '房东' },
  { value: '2', label: '租客' },
  { value: 'house_admin', label: '房源管理员' },
];

const mapListQuery = (sp) => {
  const keyword = (sp.keyword || '').trim();
  let user_type;
  let house_admin_only = false;
  if (sp.role === 'house_admin') {
    house_admin_only = true;
  } else if (sp.role) {
    user_type = sp.role;
  }
  return { keyword, user_type, house_admin_only };
};

const UserList = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useState({
    keyword: '',
    role: '',
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [displayColumns, setDisplayColumns] = useState([]);
  const [virtualModalOpen, setVirtualModalOpen] = useState(false);
  const [virtualResult, setVirtualResult] = useState(null);
  const [virtualSubmitting, setVirtualSubmitting] = useState(false);
  const [virtualForm] = Form.useForm();
  const [virtualUserList, setVirtualUserList] = useState([]);
  const [virtualUserSearchHits, setVirtualUserSearchHits] = useState([]);
  const [virtualUserSearchInput, setVirtualUserSearchInput] = useState('');
  const virtualUserSearchTimerRef = useRef(null);
  // const [voiceTestingId, setVoiceTestingId] = useState(null); // [已弃用] 语音电话测试按钮
  const [voiceNotifyLoadingId, setVoiceNotifyLoadingId] = useState(null);
  const [voiceQueryLoadingId, setVoiceQueryLoadingId] = useState(null);
  const [smsNotifyLoadingId, setSmsNotifyLoadingId] = useState(null);
  const [smsQueryLoadingId, setSmsQueryLoadingId] = useState(null);
  const [voiceResultModal, setVoiceResultModal] = useState({ open: false, title: '', content: '' });
  const [voiceTodayModal, setVoiceTodayModal] = useState({
    open: false,
    loading: false,
    dayLabel: '今日',
    date: '',
    phone: '',
    list: [],
    msg: '',
  });
  const [smsTodayModal, setSmsTodayModal] = useState({
    open: false,
    loading: false,
    dayLabel: '今日',
    date: '',
    phone: '',
    list: [],
    msg: '',
  });
  const watchedSourceUserId = Form.useWatch('source_user_id', virtualForm);

  const showVoiceResultModal = useCallback((title, content) => {
    setVoiceResultModal({ open: true, title, content: content || '' });
  }, []);

  const fetchPage = useCallback(async (page, pageSize, sp) => {
    const effective = sp ?? searchParams;
    setLoading(true);
    try {
      const { keyword, user_type, house_admin_only } = mapListQuery(effective);
      const res = await getUsers({
        page,
        page_size: pageSize,
        keyword,
        user_type,
        house_admin_only,
      });
      if (res?.success && Array.isArray(res.data)) {
        setUsers(res.data);
        setPagination({
          current: res.page || page,
          pageSize: res.page_size || pageSize,
          total: Number(res.total) || 0,
        });
      } else {
        setUsers([]);
        setPagination((prev) => ({ ...prev, current: 1, total: 0 }));
        message.error('用户列表数据格式错误');
      }
    } catch (error) {
      console.error('加载用户列表失败：', error);
      message.error('加载用户列表失败，请稍后重试');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  const handleVirtualUserSelectSearch = useCallback((keyword) => {
    const raw = keyword ?? '';
    const kw = String(raw).trim();
    setVirtualUserSearchInput(raw);
    clearTimeout(virtualUserSearchTimerRef.current);
    if (!kw) {
      setVirtualUserSearchHits([]);
      return;
    }
    virtualUserSearchTimerRef.current = setTimeout(async () => {
      try {
        const res = await getUsers({ page: 1, page_size: 100, keyword: kw });
        const incoming = res?.success && Array.isArray(res.data) ? res.data : [];
        setVirtualUserSearchHits(incoming);
        setVirtualUserList((prev) => {
          const m = new Map(prev.map((u) => [Number(u.id), u]));
          for (const u of incoming) m.set(Number(u.id), u);
          return Array.from(m.values());
        });
      } catch (err) {
        console.error('搜索用户失败：', err);
      }
    }, 300);
  }, []);

  const virtualUserSelectOptions = useMemo(() => {
    const idSet = new Set();
    const cur = Number(watchedSourceUserId);
    if (cur > 0) idSet.add(cur);
    const kw = String(virtualUserSearchInput).trim();
    if (kw) {
      virtualUserSearchHits.forEach((u) => {
        const n = Number(u.id);
        if (n > 0) idSet.add(n);
      });
    }
    return Array.from(idSet)
      .map((id) => virtualUserList.find((u) => Number(u.id) === id))
      .filter(Boolean)
      .map((user) => ({
        value: user.id,
        label: formatAdminUserSelectLabel(user),
      }));
  }, [watchedSourceUserId, virtualUserSearchInput, virtualUserSearchHits, virtualUserList]);

  const openVirtualModal = () => {
    virtualForm.resetFields();
    setVirtualUserSearchInput('');
    setVirtualUserSearchHits([]);
    setVirtualModalOpen(true);
  };

  const handleCreateVirtualUser = async () => {
    try {
      const values = await virtualForm.validateFields();
      const sourceUserId = Number(values.source_user_id);
      if (!sourceUserId) {
        message.error('请选择归属房东');
        return;
      }
      const payload = { source_user_id: sourceUserId };
      const extId = values.huifu_ext_id != null && values.huifu_ext_id !== '' ? Number(values.huifu_ext_id) : 0;
      if (extId > 0) payload.huifu_ext_id = extId;

      setVirtualSubmitting(true);
      const res = await createVirtualCollectorUser(payload);
      if (!res?.success) {
        message.error(res?.msg || '创建失败');
        return;
      }
      setVirtualModalOpen(false);
      setVirtualResult(res.data || null);
      fetchPage(pagination.current, pagination.pageSize, searchParams);
    } catch (err) {
      if (!err?.errorFields) {
        const errMsg = err?.response?.data?.msg || err?.message || '创建失败';
        message.error(errMsg);
      }
    } finally {
      setVirtualSubmitting(false);
    }
  };

  const copyText = async (text, label) => {
    const s = String(text ?? '');
    if (!s) return;
    try {
      await navigator.clipboard.writeText(s);
      message.success(`已复制${label}`);
    } catch {
      message.warning(`请手动复制：${s}`);
    }
  };

  const handleDelete = useCallback(
    async (id, nickname) => {
      console.log('触发删除逻辑：', { id, nickname });
      try {
        setLoading(true);
        await deleteUser(id);
        message.success('删除用户成功');
        fetchPage(pagination.current, pagination.pageSize, searchParams);
      } catch (error) {
        console.error('删除用户失败详情：', error);
        const errMsg = error.response?.data?.msg || error.message || '删除失败';
        message.error(`删除用户失败：${errMsg}`);
      } finally {
        setLoading(false);
      }
    },
    [fetchPage, pagination.current, pagination.pageSize, searchParams],
  );

  const patchUserVoiceStats = useCallback((userId, total, pending) => {
    setUsers((prev) =>
      prev.map((u) =>
        u.id === userId
          ? {
              ...u,
              voice_low_balance_total: total,
              voice_low_balance_pending: pending,
            }
          : u,
      ),
    );
  }, []);

  // [已弃用] 同步占位测试外呼
  // const handleVoiceTest = useCallback(async (record) => {
  //   if (!record?.voice_test_eligible) return;
  //   const uid = record.id;
  //   setVoiceTestingId(uid);
  //   try {
  //     const res = await adminTestVoiceCall(uid);
  //     if (res?.success) {
  //       const callId = res?.data?.call_id;
  //       message.success(callId ? `已发起外呼，CallId: ${callId}` : (res.msg || '已发起外呼'));
  //     } else {
  //       message.error(res?.msg || '语音外呼失败');
  //     }
  //   } catch (error) {
  //     const errMsg = error.response?.data?.msg || error.message || '语音外呼失败';
  //     message.error(errMsg);
  //   } finally {
  //     setVoiceTestingId(null);
  //   }
  // }, []);

  const handleSendVoiceLowBalance = useCallback(
    async (record) => {
      if (!record?.voice_test_eligible) return;
      const subscribed =
        record.voice_notify_subscribed === true || record.voice_notify_subscribed === 1;
      if (!subscribed) {
        showVoiceResultModal('发送失败', '该用户未购买电话提醒服务');
        return;
      }
      const uid = record.id;
      setVoiceNotifyLoadingId(uid);
      try {
        const res = await adminResendUserVoiceLowBalance(uid);
        if (res?.success) {
          const n = Number(res?.data?.enqueued) || 0;
          showVoiceResultModal('发送成功', res.msg || `已入队 ${n} 通语音，请留意手机响铃（多表约 60 秒间隔）`);
        } else {
          showVoiceResultModal('发送失败', res?.msg || '语音补发失败');
        }
      } catch (error) {
        const errMsg = error.response?.data?.msg || error.message || '语音补发失败';
        showVoiceResultModal('发送失败', errMsg);
      } finally {
        setVoiceNotifyLoadingId(null);
      }
    },
    [showVoiceResultModal],
  );

  const voiceAnswerTagColor = (status) => {
    switch (status) {
      case 'answered':
        return 'success';
      case 'unanswered':
      case 'send_failed':
      case 'query_error':
        return 'error';
      case 'syncing':
      case 'dialing':
        return 'processing';
      case 'pending_queue':
        return 'warning';
      default:
        return 'default';
    }
  };

  const handleQueryVoiceCallsByDay = useCallback(async (record, day = 'today') => {
    if (!record?.voice_test_eligible) return;
    const uid = record.id;
    const dayLabel = day === 'yesterday' ? '昨日' : '今日';
    setVoiceQueryLoadingId(uid);
    setVoiceTodayModal({
      open: true,
      loading: true,
      dayLabel,
      date: '',
      phone: record.phone || '',
      list: [],
      msg: '',
    });
    try {
      const res = await adminQueryUserVoiceCallsToday(uid, day);
      const data = res?.data || {};
      console.log(`[voice-calls-${day}] 完整接口返回:`, res);
      if (Array.isArray(data.list)) {
        data.list.forEach((row) => {
          console.log(`[voice-calls-${day}] 序号${row.seq} call_id=${row.call_id}`, row.debug_queries);
        });
      }
      setVoiceTodayModal({
        open: true,
        loading: false,
        dayLabel: data.dayLabel || dayLabel,
        date: data.date || '',
        phone: data.phone || record.phone || '',
        list: Array.isArray(data.list) ? data.list : [],
        msg: res?.msg || '',
      });
      if (!res?.success) {
        message.warning(res?.msg || '查询未完成');
      }
    } catch (error) {
      const errMsg = error.response?.data?.msg || error.message || '查询失败';
      setVoiceTodayModal({
        open: true,
        loading: false,
        dayLabel,
        date: '',
        phone: record.phone || '',
        list: [],
        msg: errMsg,
      });
      message.error(errMsg);
    } finally {
      setVoiceQueryLoadingId(null);
    }
  }, []);

  const handleQueryVoiceCallsToday = useCallback(
    (record) => handleQueryVoiceCallsByDay(record, 'today'),
    [handleQueryVoiceCallsByDay],
  );

  const handleQueryVoiceCallsYesterday = useCallback(
    (record) => handleQueryVoiceCallsByDay(record, 'yesterday'),
    [handleQueryVoiceCallsByDay],
  );

  const handleResetVoiceLowBalanceFlag = useCallback(
    async (record) => {
      if (!record?.voice_test_eligible) return;
      const uid = record.id;
      setVoiceNotifyLoadingId(uid);
      try {
        const res = await adminResetUserVoiceLowBalanceFlag(uid);
        if (res?.success) {
          message.success(res.msg || '标记已重置');
          const total = Number(record.voice_low_balance_total) || 0;
          patchUserVoiceStats(uid, total, total);
        } else {
          message.error(res?.msg || '重置失败');
        }
      } catch (error) {
        const errMsg = error.response?.data?.msg || error.message || '重置失败';
        message.error(errMsg);
      } finally {
        setVoiceNotifyLoadingId(null);
      }
    },
    [patchUserVoiceStats],
  );

  const smsStatusTagColor = (key) => {
    if (key === 'success') return 'success';
    if (key === 'failed') return 'error';
    if (key === 'waiting') return 'warning';
    return 'default';
  };

  const handleSendSmsLowBalance = useCallback(
    async (record) => {
      if (!record?.voice_test_eligible) return;
      const uid = record.id;
      setSmsNotifyLoadingId(uid);
      try {
        const res = await adminResendUserSmsLowBalance(uid);
        if (res?.success) {
          const n = Number(res?.data?.sent) || 0;
          showVoiceResultModal('短信已提交', res.msg || `已同步提交 ${n} 条短信（不进语音队列/战役）`);
        } else {
          showVoiceResultModal('短信发送失败', res?.msg || '短信补发失败');
        }
      } catch (error) {
        const errMsg = error.response?.data?.msg || error.message || '短信补发失败';
        showVoiceResultModal('短信发送失败', errMsg);
      } finally {
        setSmsNotifyLoadingId(null);
      }
    },
    [showVoiceResultModal],
  );

  const handleQuerySmsByDay = useCallback(async (record, day = 'today') => {
    if (!record?.voice_test_eligible) return;
    const uid = record.id;
    const dayLabel = day === 'yesterday' ? '昨日' : '今日';
    setSmsQueryLoadingId(uid);
    setSmsTodayModal({
      open: true,
      loading: true,
      dayLabel,
      date: '',
      phone: record.phone || '',
      list: [],
      msg: '',
    });
    try {
      const res = await adminQueryUserSmsDetails(uid, day);
      const data = res?.data || {};
      setSmsTodayModal({
        open: true,
        loading: false,
        dayLabel: data.dayLabel || dayLabel,
        date: data.date || '',
        phone: data.phone || record.phone || '',
        list: Array.isArray(data.list) ? data.list : [],
        msg: res?.msg || '',
      });
      if (!res?.success) {
        message.warning(res?.msg || '查询未完成');
      }
    } catch (error) {
      const errMsg = error.response?.data?.msg || error.message || '查询失败';
      setSmsTodayModal({
        open: true,
        loading: false,
        dayLabel,
        date: '',
        phone: record.phone || '',
        list: [],
        msg: errMsg,
      });
      message.error(errMsg);
    } finally {
      setSmsQueryLoadingId(null);
    }
  }, []);

  const handleQuerySmsToday = useCallback(
    (record) => handleQuerySmsByDay(record, 'today'),
    [handleQuerySmsByDay],
  );

  const handleQuerySmsYesterday = useCallback(
    (record) => handleQuerySmsByDay(record, 'yesterday'),
    [handleQuerySmsByDay],
  );

  // 原始表格列配置（依赖上方 handleDelete）
  const originalColumns = useMemo(
    () => [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
    },
    {
      title: '姓名',
      dataIndex: 'real_name',
      key: 'real_name',
      width: 100,
      render: (t) => (t && String(t).trim() ? t : '—'),
    },
    {
      title: '昵称',
      dataIndex: 'nickname',
      key: 'nickname',
      width: 100,
      render: (nickname) => nickname || '未知用户',
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
      width: 120,
      render: (phone) => phone || '未绑定',
    },
    {
      title: '角色',
      key: 'role',
      render: (_, record) => {
        const ut = Number(record.user_type);
        const config =
          Number.isFinite(ut) && ROLE_CONFIG[ut] != null ? ROLE_CONFIG[ut] : ROLE_CONFIG[0];
        const extraHouseAdmin = isLandlordDesignatedHouseAdmin(record);
        return (
          <Space size={[8, 6]} wrap align="center">
            <Tag color={config.color}>{config.label}</Tag>
            {extraHouseAdmin ? (
              <Tag
                style={HOUSE_ADMIN_TAG_STYLE}
                title="房东在房源中指定的子管理员（与账号类型「管理员」系统账号不同）"
              >
                管理员
              </Tag>
            ) : null}
          </Space>
        );
      },
      width: 200,
    },
    {
      title: '状态',
      key: 'status',
      render: (_, record) => {
        const config = STATUS_CONFIG[record.status] || STATUS_CONFIG[0];
        return <Tag color={config.color}>{config.label}</Tag>;
      },
      width: 80,
    },
    {
      title: '租客姓名',
      dataIndex: 'backup_real_name',
      key: 'backup_real_name',
      width: 100,
      render: renderBackupText,
    },
    {
      title: '租客身份证',
      dataIndex: 'backup_id_card',
      key: 'backup_id_card',
      width: 170,
      render: renderBackupText,
    },
    {
      title: '身份证正面',
      dataIndex: 'backup_idcard_front',
      key: 'backup_idcard_front',
      width: 90,
      render: (url) => renderBackupIdCardImage(url, '身份证正面'),
    },
    {
      title: '身份证反面',
      dataIndex: 'backup_idcard_back',
      key: 'backup_idcard_back',
      width: 90,
      render: (url) => renderBackupIdCardImage(url, '身份证反面'),
    },
    {
      title: '入住状态',
      dataIndex: 'backup_checkin_status_text',
      key: 'backup_checkin_status_text',
      width: 110,
      render: (text) => renderBackupStatusTag(text, BACKUP_CHECKIN_STATUS_COLORS),
    },
    {
      title: '实名状态',
      dataIndex: 'backup_identity_status_text',
      key: 'backup_identity_status_text',
      width: 130,
      render: (text) => renderBackupStatusTag(text, BACKUP_IDENTITY_STATUS_COLORS),
    },
    {
      title: 'OpenID',
      dataIndex: 'openid',
      key: 'openid',
      width: 180,
      render: (openid) => openid || '未绑定',
    },
    {
      title: '公众号OpenID',
      dataIndex: 'mp_openid',
      key: 'mp_openid',
      width: 180,
      render: (mpOpenid) => mpOpenid || '未绑定',
    },
    {
      title: 'UnionID',
      dataIndex: 'unionid',
      key: 'unionid',
      width: 180,
      render: (unionid) => unionid || '未绑定',
    },
    {
      title: '语音提醒',
      key: 'voice_notify',
      width: 320,
      render: (_, record) => {
        if (!record.voice_test_eligible) return '—';
        const loading = voiceNotifyLoadingId === record.id;
        const smsLoading = smsNotifyLoadingId === record.id;
        const total = Number(record.voice_low_balance_total) || 0;
        const pending = Number(record.voice_low_balance_pending) || 0;
        const subscribed = !!record.voice_notify_subscribed;
        const activeDevices = Number(record.voice_notify_device_active_count) || 0;
        const activeMonths = Number(record.voice_notify_active_months) || 0;
        const paidUntil = formatVoiceNotifyYm(record.voice_notify_paid_until_ym);
        let label = '无低电量表';
        let tagColor = 'default';
        if (total > 0) {
          if (pending === 0) {
            label = '已提醒';
            tagColor = 'green';
          } else {
            label = `待提醒 ${pending}/${total}`;
            tagColor = 'warning';
          }
        }
        const payLabel = subscribed
          ? `已开通${activeDevices > 0 ? `${activeDevices}台` : ''}${paidUntil ? `（至${paidUntil}）` : activeMonths > 0 ? `（${activeMonths}个月）` : ''}`
          : '未购买电话提醒';
        const payTagColor = subscribed ? 'blue' : 'default';
        return (
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <Tag color={payTagColor} style={{ margin: 0 }}>
              {payLabel}
            </Tag>
            <Tag color={tagColor} style={{ margin: 0 }}>
              {label}
            </Tag>
            <Space size={4} wrap>
              <Button
                size="small"
                type="primary"
                loading={loading}
                onClick={() => handleSendVoiceLowBalance(record)}
              >
                发送语音提醒
              </Button>
              <Button
                size="small"
                loading={voiceQueryLoadingId === record.id}
                onClick={() => handleQueryVoiceCallsYesterday(record)}
              >
                查询昨日接听
              </Button>
              <Button
                size="small"
                loading={voiceQueryLoadingId === record.id}
                onClick={() => handleQueryVoiceCallsToday(record)}
              >
                查询今日接听
              </Button>
              <Button
                size="small"
                type="primary"
                loading={smsLoading}
                onClick={() => handleSendSmsLowBalance(record)}
              >
                发送短信提醒
              </Button>
              <Button
                size="small"
                loading={smsQueryLoadingId === record.id}
                onClick={() => handleQuerySmsYesterday(record)}
              >
                查询昨日短信
              </Button>
              <Button
                size="small"
                loading={smsQueryLoadingId === record.id}
                onClick={() => handleQuerySmsToday(record)}
              >
                查询今日短信
              </Button>
              <Button
                size="small"
                loading={loading}
                onClick={() => handleResetVoiceLowBalanceFlag(record)}
              >
                重置标记
              </Button>
            </Space>
          </Space>
        );
      },
    },
    // [已弃用] 占位测试外呼按钮
    // {
    //   title: '测试',
    //   key: 'voice_test',
    //   width: 100,
    //   render: (_, record) =>
    //     record.voice_test_eligible ? (
    //       <Button
    //         type="link"
    //         size="small"
    //         loading={voiceTestingId === record.id}
    //         onClick={() => handleVoiceTest(record)}
    //       >
    //         语音电话
    //       </Button>
    //     ) : (
    //       '—'
    //     ),
    // },
    {
      title: '操作',
      key: 'action',
      width: 140,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            onClick={() => navigate(`/user/role/${record.id}`)}
          >
            修改
          </Button>
          <Button
            type="text"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id, record.nickname)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ],
    [
      navigate,
      handleDelete,
      handleSendVoiceLowBalance,
      handleResetVoiceLowBalanceFlag,
      handleQueryVoiceCallsToday,
      handleQueryVoiceCallsYesterday,
      handleSendSmsLowBalance,
      handleQuerySmsToday,
      handleQuerySmsYesterday,
      voiceNotifyLoadingId,
      voiceQueryLoadingId,
      smsNotifyLoadingId,
      smsQueryLoadingId,
    ],
    // [navigate, handleDelete, handleVoiceTest, voiceTestingId],
  );

  // 从localStorage加载列设置并应用到表格（只处理隐藏）
  useEffect(() => {
    const storageKey = 'userList_columnStates';
    const saved = localStorage.getItem(storageKey);
    let columnStates = {};

    if (saved) {
      try {
        columnStates = JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse column states:', e);
      }
    }

    const processedColumns = originalColumns
      .map((col) => {
        const state = columnStates[col.key];
        if (state === 'hidden') {
          return null;
        }
        return col;
      })
      .filter((col) => col !== null);

    setDisplayColumns(processedColumns);
  }, [originalColumns]);

  // 进入页面即加载第一页（服务端分页，适合大量用户）；筛选与翻页见下方逻辑
  useEffect(() => {
    fetchPage(1, pagination.pageSize, { keyword: '', role: '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 仅挂载时拉首屏，避免 keyword 输入时反复请求
  }, []);

  const handleToolbarSearch = () => {
    fetchPage(1, pagination.pageSize, searchParams);
  };

  const handleKeywordChange = (value) => {
    setSearchParams({
      ...searchParams,
      keyword: value,
    });
  };

  const handleRoleChange = (value) => {
    const next = { ...searchParams, role: value || '' };
    setSearchParams(next);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchPage(1, pagination.pageSize, next);
  };

  const handleReset = () => {
    const cleared = { keyword: '', role: '' };
    setSearchParams(cleared);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchPage(1, pagination.pageSize, cleared);
  };

  const handleRefresh = () => {
    fetchPage(pagination.current, pagination.pageSize, searchParams);
  };

  const handleExport = () => {
    const exportData = users.map((user) => ({
      ID: user.id,
      姓名: (user.real_name && String(user.real_name).trim()) || '—',
      昵称: user.nickname || '未知用户',
      手机号: user.phone || '未绑定',
      状态: STATUS_CONFIG[user.status]?.label || '未知',
      租客姓名: (user.backup_real_name && String(user.backup_real_name).trim()) || '—',
      租客身份证: (user.backup_id_card && String(user.backup_id_card).trim()) || '—',
      入住状态: (user.backup_checkin_status_text && String(user.backup_checkin_status_text).trim()) || '—',
      实名状态: (user.backup_identity_status_text && String(user.backup_identity_status_text).trim()) || '—',
      身份证正面: user.backup_idcard_front || '—',
      身份证反面: user.backup_idcard_back || '—',
      基础角色: ROLE_CONFIG[Number(user.user_type)]?.label || '未知角色',
      房源管理员: isLandlordDesignatedHouseAdmin(user) ? '是' : '否',
      OpenID: user.openid || '未绑定',
      公众号OpenID: user.mp_openid || '未绑定',
      UnionID: user.unionid || '未绑定',
    }));

    if (exportData.length === 0) {
      message.warning('当前页没有可导出的用户');
      return;
    }
    if (exportToCSV(exportData, '用户列表')) {
      message.success(`已导出当前页 ${exportData.length} 条`);
    } else {
      message.error('导出数据失败，请重试');
    }
  };

  const handleTableChange = (newPagination) => {
    fetchPage(newPagination.current, newPagination.pageSize, searchParams);
  };

  return (
    <div style={{ padding: '20px 0' }}>
      <PageTitle title="用户列表" />

      <ListToolbar
        searchPlaceholder="可选：姓名/昵称/手机/用户ID/openid，点「搜索」筛选当前列表"
        searchValue={searchParams.keyword}
        onSearchChange={handleKeywordChange}
        onSearch={handleToolbarSearch}
        onReset={handleReset}
        onRefresh={handleRefresh}
        onExport={handleExport}
        beforeFilters={
          <Button type="default" size="small" icon={<UserAddOutlined />} disabled>
            新建虚拟收款账户（开发中）
          </Button>
        }
        filters={[
          {
            key: 'role',
            label: '角色筛选',
            value: searchParams.role,
            onChange: handleRoleChange,
            options: ROLE_FILTER_OPTIONS,
          },
        ]}
      />

      <ResizableTable
        columns={displayColumns.length > 0 ? displayColumns : originalColumns}
        dataSource={users}
        loading={loading}
        rowKey="id"
        storageKey="userList"
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条数据`,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        onChange={handleTableChange}
        scroll={{ x: 1800 }}
        locale={{
          emptyText: (
            <Empty description="暂无用户数据；可调整角色筛选或输入关键字后点「搜索」。" />
          ),
        }}
        size="small"
      />

      <Modal
        title="新建虚拟收款用户"
        open={virtualModalOpen}
        onCancel={() => setVirtualModalOpen(false)}
        onOk={handleCreateVirtualUser}
        confirmLoading={virtualSubmitting}
        destroyOnClose
        maskClosable={false}
        okText="创建"
      >
        <Form form={virtualForm} layout="vertical">
          <Form.Item
            name="source_user_id"
            label="归属房东（真实用户）"
            rules={[{ required: true, message: '请选择归属房东' }]}
            extra="将写入 wechat_id：VIRT_SRC_{房东用户ID}"
          >
            <Select
              placeholder="输入姓名/昵称/手机/ID 搜索"
              showSearch
              filterOption={false}
              onSearch={handleVirtualUserSelectSearch}
              notFoundContent={
                String(virtualUserSearchInput).trim() ? '未找到用户' : '请输入关键词搜索'
              }
              options={virtualUserSelectOptions}
            />
          </Form.Item>
          <Form.Item
            name="huifu_ext_id"
            label="汇付账号表 ID（可选）"
            extra="若填写，openid 为 VIRT_HUIFU_{该ID}、昵称为 代收#{该ID}（与历史代收号一致）；不填则用新建用户的 ID"
          >
            <InputNumber min={1} precision={0} style={{ width: '100%' }} placeholder="fangdong_ext 表主键，如 490" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="虚拟收款用户已创建"
        open={!!virtualResult}
        onCancel={() => setVirtualResult(null)}
        footer={[
          <Button key="close" type="primary" onClick={() => setVirtualResult(null)}>
            知道了
          </Button>,
        ]}
        maskClosable={false}
      >
        {virtualResult ? (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Typography.Text>
              请复制下方<strong>虚拟用户 ID</strong>，再到汇付账号管理、汇付银行卡管理修改「用户ID」，并在分账配置中指定房源收账人。
            </Typography.Text>
            <div>
              <Typography.Text type="secondary">虚拟用户 ID（用于绑定汇付账号/银行卡/收账人）</Typography.Text>
              <div style={{ marginTop: 8 }}>
                <Typography.Text copyable={{ text: String(virtualResult.id) }} style={{ fontSize: 20 }}>
                  {virtualResult.id}
                </Typography.Text>
                <Button type="link" size="small" onClick={() => copyText(virtualResult.id, '用户 ID')}>
                  复制
                </Button>
              </div>
            </div>
            <div>
              <Typography.Text type="secondary">OpenID</Typography.Text>
              <div><Typography.Text code>{virtualResult.openid}</Typography.Text></div>
            </div>
            <div>
              <Typography.Text type="secondary">昵称</Typography.Text>
              <div><Typography.Text>{virtualResult.nickname}</Typography.Text></div>
            </div>
            <div>
              <Typography.Text type="secondary">归属标记（wechat_id）</Typography.Text>
              <div><Typography.Text code>{virtualResult.wechat_id}</Typography.Text></div>
            </div>
          </Space>
        ) : null}
      </Modal>

      <Modal
        title={voiceResultModal.title || '语音提醒'}
        open={voiceResultModal.open}
        footer={[
          <Button key="ok" type="primary" onClick={() => setVoiceResultModal((m) => ({ ...m, open: false }))}>
            知道了
          </Button>,
        ]}
        onCancel={() => setVoiceResultModal((m) => ({ ...m, open: false }))}
        maskClosable
      >
        {voiceResultModal.content}
      </Modal>

      <Modal
        title={`${voiceTodayModal.dayLabel || '今日'}语音接听查询`}
        open={voiceTodayModal.open}
        width={720}
        footer={[
          <Button key="close" type="primary" onClick={() => setVoiceTodayModal((m) => ({ ...m, open: false }))}>
            关闭
          </Button>,
        ]}
        onCancel={() => setVoiceTodayModal((m) => ({ ...m, open: false }))}
        maskClosable
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
          {voiceTodayModal.date ? `日期：${voiceTodayModal.date}` : `按${voiceTodayModal.dayLabel || '今日'}语音队列入队顺序查询`}
          {voiceTodayModal.phone ? ` · 手机：${voiceTodayModal.phone}` : ''}
          {voiceTodayModal.msg ? ` · ${voiceTodayModal.msg}` : ''}
        </Typography.Paragraph>
        <Table
          size="small"
          rowKey={(row) => String(row.queue_id || row.seq)}
          loading={voiceTodayModal.loading}
          dataSource={voiceTodayModal.list}
          pagination={false}
          locale={{ emptyText: voiceTodayModal.loading ? '查询中…' : `${voiceTodayModal.dayLabel || '今日'}暂无语音记录` }}
          columns={[
            {
              title: '序号',
              dataIndex: 'seq',
              width: 56,
            },
            {
              title: '入队时间',
              dataIndex: 'queued_at',
              width: 150,
            },
            {
              title: '渠道',
              dataIndex: 'channel_text',
              width: 72,
              render: (text, row) => text || (row.channel === 'exclusive' ? '专属' : '公共') || '—',
            },
            {
              title: '房源',
              dataIndex: 'fang',
              ellipsis: true,
            },
            {
              title: '发送',
              dataIndex: 'queue_status_text',
              width: 80,
            },
            {
              title: '接听结果',
              dataIndex: 'answer_label',
              render: (text, row) => (
                <Tag color={voiceAnswerTagColor(row.answer_status)} style={{ margin: 0 }}>
                  {text || '—'}
                </Tag>
              ),
            },
          ]}
        />
        <Typography.Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0, fontSize: 12 }}>
          调试：按 F12 打开控制台，搜索 <Typography.Text code>voice-calls-today</Typography.Text>，
          可查看每条记录的 <Typography.Text code>debug_queries</Typography.Text>（含两种 ProdId 的阿里云原始返回 data_raw）。
        </Typography.Paragraph>
      </Modal>

      <Modal
        title={`${smsTodayModal.dayLabel || '今日'}短信送达查询`}
        open={smsTodayModal.open}
        width={900}
        footer={[
          <Button key="close" type="primary" onClick={() => setSmsTodayModal((m) => ({ ...m, open: false }))}>
            关闭
          </Button>,
        ]}
        onCancel={() => setSmsTodayModal((m) => ({ ...m, open: false }))}
        maskClosable
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
          {smsTodayModal.date ? `日期：${smsTodayModal.date}` : ''}
          {smsTodayModal.phone ? ` · 手机：${smsTodayModal.phone}` : ''}
          {smsTodayModal.msg ? ` · ${smsTodayModal.msg}` : ''}
        </Typography.Paragraph>
        <Table
          size="small"
          rowKey={(row) => `${row.seq}-${row.send_date}-${row.out_id || ''}-${row.template_code || ''}`}
          loading={smsTodayModal.loading}
          dataSource={smsTodayModal.list}
          pagination={false}
          scroll={{ x: 860 }}
          locale={{ emptyText: smsTodayModal.loading ? '查询中…' : `${smsTodayModal.dayLabel || '今日'}暂无短信记录` }}
          columns={[
            { title: '序号', dataIndex: 'seq', width: 56 },
            { title: '发送时间', dataIndex: 'send_date', width: 150 },
            { title: '接收时间', dataIndex: 'receive_date', width: 150 },
            {
              title: '状态',
              dataIndex: 'status_label',
              width: 140,
              render: (text, row) => (
                <Tag color={smsStatusTagColor(row.status_key)} style={{ margin: 0, whiteSpace: 'normal' }}>
                  {text || '—'}
                </Tag>
              ),
            },
            { title: '原因', dataIndex: 'reason', ellipsis: true, width: 220 },
            { title: 'ErrCode', dataIndex: 'err_code', width: 120 },
            { title: '模板', dataIndex: 'template_code', width: 130 },
            { title: '内容', dataIndex: 'content', ellipsis: true },
          ]}
        />
      </Modal>
    </div>
  );
};

export default UserList;
